import React, { useEffect, useRef, useState } from 'react';

import { Button, ButtonTheme } from '@imgly/imgly-website-components';
import classNames from 'classnames';
import { useCopyToClipboard } from 'react-use';

import { CodeLanguage } from '../../../../types';
import { ReactComponent as CheckmarkIcon } from '../../../assets/images/icons/Checkmark.svg';
import { ReactComponent as CopyIcon } from '../../../assets/images/icons/Copy.svg';

import * as classes from './style.module.scss';

interface Props {
  show: boolean;
  codeLanguage: CodeLanguage | string;
  codeRef: React.MutableRefObject<HTMLPreElement | null>;
  codeBlockIndex?: number;
}

export const CodeBlockCopyButton: React.FC<Props> = ({
  show,
  codeLanguage,
  codeRef,
  codeBlockIndex,
}) => {
  const [status, setStatus] = useState(false);
  const [{ value: copied }, copyToClipboard] = useCopyToClipboard();
  const prevCodeBlockIndex = useRef<number>();

  const handleCopy = () => {
    if (codeRef.current) {
      copyToClipboard(`${codeRef.current.innerText}\n\n`);
    }
  };

  useEffect(() => {
    let timer: NodeJS.Timeout | null = null;

    // reset the copy button if the file was changed
    if (codeBlockIndex && codeBlockIndex !== prevCodeBlockIndex.current) {
      setStatus(false);
      prevCodeBlockIndex.current = codeBlockIndex;
      if (timer) clearTimeout(timer);
    } else {
      setStatus(!!copied);
      // automatically reset the copy button after a couple of seconds
      timer = setTimeout(() => {
        setStatus(false);
      }, 1000);
    }

    return () => {
      if (timer) clearTimeout(timer);
    };
  }, [codeBlockIndex, copied]);

  // The copy button for diff snippets is disabled since we don't have sanitization for diffs in place.
  // also see this comment: https://github.com/facebook/react-native-website/pull/2984#issuecomment-1059064777
  if (codeLanguage === 'diff') {
    return null;
  }

  return (
    <div className={classes.root}>
      <Button
        className={classNames(classes.button, { [classes.button__show]: show })}
        aria-label="Copy code"
        onClick={handleCopy}
        theme={ButtonTheme.Secondary}
        leadingIcon={status ? <CheckmarkIcon /> : <CopyIcon />}
        small
      />
    </div>
  );
};
